export const CAMPAIGN_DETAIL_BROWSER_DEADLINE_MS = 18_000;

export function campaignDetailReadScopeKey(companyId, campaignId, datePreset) {
  if (!companyId || !campaignId || !datePreset) return '';
  return JSON.stringify([companyId, campaignId, datePreset]);
}

function readFailure(code, message, name = 'AbortError') {
  const error = new Error(message);
  error.name = name;
  error.code = code;
  return error;
}

function timeoutFailure() {
  return readFailure(
    'curated_campaign_detail_timeout',
    'Campaign detail exceeded its browser deadline.',
    'TimeoutError',
  );
}

function abortedFailure() {
  return readFailure(
    'curated_campaign_detail_aborted',
    'Campaign detail read was superseded.',
  );
}

export function isCurrentCampaignDetailRead({
  mounted,
  currentScope,
  expectedScope,
  currentRequestId,
  requestId,
}) {
  return mounted === true
    && Boolean(expectedScope)
    && currentScope === expectedScope
    && currentRequestId === requestId;
}

export function createCampaignDetailReadCoordinator({
  deadlineMs = CAMPAIGN_DETAIL_BROWSER_DEADLINE_MS,
  schedule = setTimeout,
  cancelSchedule = clearTimeout,
} = {}) {
  if (!Number.isFinite(deadlineMs) || deadlineMs <= 0) {
    throw new TypeError('Campaign detail deadline must be positive.');
  }
  if (typeof schedule !== 'function' || typeof cancelSchedule !== 'function') {
    throw new TypeError('Campaign detail scheduler is unavailable.');
  }

  const entries = new Map();
  let nextRequestId = 0;

  function clearEntryTimers(entry) {
    if (entry.deadlineTimer) cancelSchedule(entry.deadlineTimer);
    if (entry.abortTimer) cancelSchedule(entry.abortTimer);
    entry.deadlineTimer = null;
    entry.abortTimer = null;
  }

  function abortEntry(entry, error = abortedFailure()) {
    if (!entry || entries.get(entry.scope) !== entry) return false;
    entries.delete(entry.scope);
    clearEntryTimers(entry);
    if (!entry.controller.signal.aborted) entry.controller.abort(error);
    entry.rejectCancellation(error);
    return true;
  }

  function read(scope, execute, { replace = false } = {}) {
    if (!scope || typeof scope !== 'string') {
      throw new TypeError('Campaign detail scope is required.');
    }
    if (typeof execute !== 'function') {
      throw new TypeError('Campaign detail executor is required.');
    }

    const existing = entries.get(scope);
    if (existing && !replace) {
      if (existing.abortTimer) {
        cancelSchedule(existing.abortTimer);
        existing.abortTimer = null;
      }
      return existing.claim;
    }
    if (existing) abortEntry(existing);

    const controller = new AbortController();
    let rejectCancellation;
    const cancellation = new Promise((_, reject) => {
      rejectCancellation = reject;
    });
    let request;
    try {
      request = Promise.resolve(execute(controller.signal));
    } catch (error) {
      request = Promise.reject(error);
    }
    const entry = {
      scope,
      controller,
      rejectCancellation,
      deadlineTimer: null,
      abortTimer: null,
      claim: null,
    };
    const requestId = ++nextRequestId;
    const promise = Promise.race([request, cancellation]).finally(() => {
      if (entries.get(scope) === entry) entries.delete(scope);
      clearEntryTimers(entry);
    });
    entry.claim = Object.freeze({ requestId, promise });
    entries.set(scope, entry);
    entry.deadlineTimer = schedule(() => {
      abortEntry(entry, timeoutFailure());
    }, deadlineMs);
    return entry.claim;
  }

  function abort(scope) {
    return abortEntry(entries.get(scope));
  }

  function scheduleAbort(scope) {
    const entry = entries.get(scope);
    if (!entry || entry.abortTimer) return false;
    entry.abortTimer = schedule(() => {
      entry.abortTimer = null;
      abortEntry(entry);
    }, 0);
    return true;
  }

  return Object.freeze({
    read,
    abort,
    scheduleAbort,
    has(scope) {
      return entries.has(scope);
    },
  });
}

// This registry stores only unresolved reads and deletes every entry on settle.
// The exact company/campaign/date key prevents cross-tenant sharing while still
// allowing StrictMode replay and immediate browser navigation to reuse a flight.
export const campaignDetailReadCoordinator = createCampaignDetailReadCoordinator();
